/**
 * Client for the login page's public platform totals (`GET /public/stats`, served pre-session by the
 * BFF: src/worker/public-stats.ts). Every figure may be `null` when its source is unavailable.
 */

export interface PublicStat {
  readonly value: number | null;
  readonly delta7dPct: number | null;
  /** The total grew from 0 in the last 7 days (no percentage exists); shown as "▲ new". */
  readonly new7d: boolean;
}

export interface PublicStats {
  readonly humans: PublicStat;
  readonly agents: PublicStat;
  readonly tether: PublicStat;
  readonly hta: PublicStat;
}

export type PublicStatsLoader = () => Promise<PublicStats | null>;

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function stat(raw: unknown): PublicStat {
  const r = raw !== null && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const value = num(r.value);
  return {
    value: value !== null && value >= 0 ? value : null,
    delta7dPct: num(r.delta_7d_pct),
    new7d: r.new_7d === true,
  };
}

/** Parse the route's JSON body; anything malformed becomes unavailable figures. */
export function parsePublicStats(body: unknown): PublicStats {
  const b = body !== null && typeof body === "object" ? (body as Record<string, unknown>) : {};
  return { humans: stat(b.humans), agents: stat(b.agents), tether: stat(b.tether), hta: stat(b.hta) };
}

/** Fetch the totals; resolves `null` on any network or HTTP failure (the page shows dashes). */
export const fetchPublicStats: PublicStatsLoader = async () => {
  try {
    const res = await fetch("/public/stats", { headers: { accept: "application/json" } });
    if (!res.ok) return null;
    return parsePublicStats(await res.json());
  } catch {
    return null;
  }
};

const FULL = new Intl.NumberFormat("en-US");
const COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/** "1,284" below 100,000, then compact ("2.1M"); an unavailable figure is an em dash. */
export function formatStatValue(value: number | null): string {
  if (value === null) return "—";
  return value < 100_000 ? FULL.format(value) : COMPACT.format(value);
}
